/**
 * Content for the templated Focus Area and Innovation pages. Each entry drives a
 * route; the shared page templates read everything they render from here, so a
 * new focus area or product only needs a record added below plus a nav link.
 */

import { PRODUCT_URLS, resolveProductUrl } from '@podus/utils';
import harmlistLogo from '@podus/ui/logo-icons/harmlist.png';
import irisLogo from '@podus/ui/logo-icons/iris.png';
import justiceClockLogo from '@podus/ui/logo-icons/justice-clock.png';
import openJusticeLogo from '@podus/ui/logo-icons/open-justice.png';
import podusAidLogo from '@podus/ui/logo-icons/podus-aid.png';
import podusAiLogo from '@podus/ui/logo-icons/podus-ai.png';
import podusAppointmentLogo from '@podus/ui/logo-icons/podus-appointment.png';
import podusContractLogo from '@podus/ui/logo-icons/podus-contract.png';
import podusMultidoorLogo from '@podus/ui/logo-icons/podus-multidoor.png';
import yourcheckaLogo from '@podus/ui/logo-icons/yourcheaka.png';

export interface FocusAreaPage {
    slug: string;
    /** Greyed lead-in of the hero title. */
    titleLead: string;
    /** Bold remainder of the hero title. */
    titleBold: string;
    /** Optional small line under the title (e.g. the SGBV expansion). */
    subtitle?: string;
    intro: string;
    /** Closing statement, dark first half. */
    statementDark: string;
    /** Closing statement, grey remainder. */
    statementGrey: string;
    /** Hero background from the CMS; the bundled one stands in until an area has its own. */
    heroImage?: string;
}

/**
 * One entry per Focus Area page. The CMS owns this list once it has answered —
 * an area it publishes is added, one it has withdrawn is dropped — so the
 * bundled five are the offline fallback, not the ceiling.
 */
export const FOCUS_AREA_PAGES: FocusAreaPage[] = [
    {
        slug: 'tech-for-justice',
        titleLead: 'Tech For',
        titleBold: 'Justice',
        intro: 'The justice system never built the tools its users needed. So we do — case tracking, plain-language guidance and open court data, designed for the people a courtroom usually leaves out.',
        statementDark:
            'A justice system is not measured by the laws it writes. It is measured by how long an ordinary person waits for one of them to be applied.',
        statementGrey:
            'That wait is where we work — closing it case by case, court by court, until access stops being a matter of luck.',
    },
    {
        slug: 'access-to-justice',
        titleLead: 'Access To',
        titleBold: 'Justice',
        intro: 'Legal help should not depend on who you know or what you earn. We place lawyers where cases actually stall, and keep the route to representation short, free and understandable.',
        statementDark:
            'A justice system is not measured by the laws it writes. It is measured by how long an ordinary person waits for one of them to be applied.',
        statementGrey:
            'That wait is where we work — closing it case by case, court by court, until access stops being a matter of luck.',
    },
    {
        slug: 'judicial-accountability',
        titleLead: 'Judicial',
        titleBold: 'Accountability',
        intro: 'A court that publishes how long it takes is a court that starts getting faster. We measure delay across every state, rank what we find, and put it where citizens and judges can both see it.',
        statementDark:
            'A justice system is not measured by the laws it writes. It is measured by how long an ordinary person waits for one of them to be applied.',
        statementGrey:
            'That wait is where we work — closing it case by case, court by court, until access stops being a matter of luck.',
    },
    {
        slug: 'democratic-governance',
        titleLead: 'Democratic',
        titleBold: 'Governance',
        intro: 'Rights are only real when institutions can be held to them. We work with citizens, civic partners and the press to make public power answerable in practice, not just on paper.',
        statementDark:
            'A justice system is not measured by the laws it writes. It is measured by how long an ordinary person waits for one of them to be applied.',
        statementGrey:
            'That wait is where we work — closing it case by case, court by court, until access stops being a matter of luck.',
    },
    {
        slug: 'sgbv',
        titleLead: '',
        titleBold: 'SGBV',
        subtitle: '(Sexual and Gender-Based Violence)',
        intro: 'Most cases fail long before trial — at the report, the medical record, the first adjournment. We build survivor-led support around those pressure points, and stay with the case until it is heard.',
        statementDark:
            'A justice system is not measured by the laws it writes. It is measured by how long an ordinary person waits for one of them to be applied.',
        statementGrey:
            'That wait is where we work — closing it case by case, court by court, until access stops being a matter of luck.',
    },
];

export const getFocusArea = (slug?: string): FocusAreaPage | undefined => FOCUS_AREA_PAGES.find((p) => p.slug === slug);

export interface InnovationPage {
    slug: string;
    /** Greyed lead-in of the hero title. */
    titleLead: string;
    /** Bold remainder of the hero title. */
    titleBold: string;
    /** Name used in the "About Citizens' Gavel …" band. */
    label: string;
    intro: string;
    /** Product mark, from `@podus/ui/logo-icons`. Shown in the page hero. */
    logo: string;
}

/**
 * One entry per product page. Like the focus areas, the CMS owns this list
 * once it has answered: a Live product has a page, a draft does not.
 */
/**
 * Where each product actually lives, for the "Ready to try …" button on its page.
 *
 * Read at call time rather than baked into a constant: `PRODUCT_URLS` resolves
 * per environment through lazy getters, so a module-level copy would freeze
 * whatever the first import happened to see.
 *
 * Checked 2026-09-23 — every URL below answers 200. Two products have no public
 * app of their own yet (Justice Clock, Judicial Accountability); their button
 * goes to the contact page rather than nowhere.
 */
export function innovationSite(slug: string): string {
    switch (slug) {
        case 'podus-ai':
            return PRODUCT_URLS.podusai;
        case 'podus-contract':
            return PRODUCT_URLS.poduscontract;
        case 'podus-appointment':
            return PRODUCT_URLS.podusappointment;
        case 'podus-legal-aid':
            // `PRODUCT_URLS.podusaid` is the signed-in dashboard, which would send
            // a visitor who has never heard of Podus Aid to a login screen. This
            // button is an introduction, so it opens the product's front page.
            return resolveProductUrl('https://app.podus.org');
        case 'podus-checker':
            return PRODUCT_URLS.poduschecker;
        case 'podus-multidoor-court':
            // `multi-door.podus.org`, which the projects data long carried, no
            // longer resolves; the live host is the one in the product map.
            return PRODUCT_URLS.podusmultidoor;
        // Gavel's own tools — not part of the Podus product map.
        case 'iris':
            return 'https://iris.citizensgavel.org';
        case 'harmlist':
            return 'https://harmlist.com';
        case 'volunteer':
            return '/about#volunteers';
        default:
            return '/help';
    }
}

export const INNOVATION_PAGES: InnovationPage[] = [
    {
        slug: 'iris',
        titleLead: 'Screening That',
        titleBold: 'Protects',
        label: 'IRIS',
        logo: irisLogo,
        intro: 'The Intimate Relationship Indicator Screening tool helps frontline responders recognise escalating danger early, using a short structured assessment instead of a judgement call made under pressure.',
    },
    {
        slug: 'justice-clock',
        titleLead: 'Timing Justice,',
        titleBold: 'Transparently',
        label: 'Justice Clock',
        logo: justiceClockLogo,
        intro: 'The clock starts the moment a case is filed and never stops. Every adjournment, every reserved judgment, every month of silence is recorded — and published where it cannot be quietly forgotten.',
    },
    {
        slug: 'volunteer',
        titleLead: 'Lend Your',
        titleBold: 'Voice',
        label: 'Volunteer',
        logo: openJusticeLogo,
        intro: 'Lawyers take on pro bono files. Everyone else helps with research, translation, outreach and data. Tell us your skills and hours, and we will match you to work that genuinely needs them.',
    },
    {
        slug: 'judicial-accountability',
        titleLead: 'Holding Power',
        titleBold: 'To Account',
        label: 'Judicial Accountability',
        logo: openJusticeLogo,
        intro: 'Court performance data, gathered from registries and freedom-of-information requests, then published openly — so that delay becomes a fact on the record rather than an anecdote.',
    },
    {
        slug: 'podus-ai',
        titleLead: 'Justice, Reimagined Through',
        titleBold: 'Technology',
        label: 'Podus AI',
        logo: podusAiLogo,
        intro: 'Ask a legal question in your own words and get a plain-language answer grounded in Nigerian law — with a clear handover to a human lawyer the moment the question needs one.',
    },
    {
        slug: 'podus-multidoor-court',
        titleLead: 'Resolution Without',
        titleBold: 'The Courtroom',
        label: 'Podus Multidoor Court',
        logo: podusMultidoorLogo,
        intro: 'Not every dispute belongs in front of a judge. Mediation and arbitration through accredited neutrals settle matters in weeks rather than years, with an outcome both sides can enforce.',
    },
    {
        slug: 'podus-appointment',
        titleLead: 'Legal Help,',
        titleBold: 'Booked In Minutes',
        label: 'Podus Appointment',
        logo: podusAppointmentLogo,
        intro: 'Find a verified lawyer in your area and practice area, see the fee before you book, and pick a time that suits you — no gatekeeping, no waiting for a call back that never comes.',
    },
    {
        slug: 'podus-checker',
        titleLead: 'Verify Before',
        titleBold: 'You Trust',
        label: 'Podus Checker',
        logo: yourcheckaLogo,
        intro: 'Confirm that a lawyer is on the roll, a company is really registered, or a document is what it claims to be — in seconds, before money or a signature changes hands.',
    },
    {
        slug: 'podus-contract',
        titleLead: 'Contract-Ready',
        titleBold: 'In Minutes',
        label: 'Podus Contract',
        logo: podusContractLogo,
        intro: 'Draft from vetted templates, get every clause explained in plain language, and sign digitally — so that agreements protect the person signing them, not only the party who drafted them.',
    },
    {
        slug: 'podus-legal-aid',
        titleLead: 'Aid For Those',
        titleBold: 'Who Need It',
        label: 'Podus Legal Aid',
        logo: podusAidLogo,
        intro: 'Tell us what happened and we will match you with a lawyer who can take it on. Free for those who qualify, and the request itself takes minutes rather than a trip across town.',
    },
    {
        slug: 'harmlist',
        titleLead: 'Check Them',
        titleBold: 'Before They Cost You',
        label: 'Harmlist',
        logo: harmlistLogo,
        intro: 'A searchable public record of loan apps and tech companies flagged for unethical practice. Look a company up before you borrow or sign, and report one that has already harmed you so the next person sees it coming.',
    },
];

export const getInnovation = (slug?: string): InnovationPage | undefined =>
    INNOVATION_PAGES.find((p) => p.slug === slug);
